import type { RouterProps } from 'wouter';
import { DashboardLayout } from 'components/Layout/DashboardLayout';
import { AccountDetailPage } from 'components/pages/payments/AccountDetailPage';
import { CatalogPage } from 'components/pages/payments/CatalogPage';
import { CreateInvoicePage } from 'components/pages/payments/CreateInvoicePage';
import { EntityPiiPage } from 'components/pages/payments/EntityPiiPage';
import { InvoiceDetailPage } from 'components/pages/payments/InvoiceDetailPage/index';
import { InvoicesListPage } from 'components/pages/payments/InvoicesListPage';
import { PaymentsPage } from 'components/pages/payments/PaymentsPage';
import { TransactionDetailPage } from 'components/pages/payments/TransactionDetailPage';
import { TransactionListPage } from 'components/pages/payments/TransactionListPage';
import { UserDetailPage } from 'components/pages/payments/UserDetailPage';
import { PaymentsHomePage } from 'components/pages/PaymentsHomePage/PaymentsHomePage';
import { useFlagEnabledWithOverrides } from 'hooks/useFlagEnabledWithOverrides';
import React, { Suspense, useCallback } from 'react';
import { makePath } from 'utils/routes';
import { Route, Router, Switch, useLocation } from 'wouter';
import { usePaymentsApiHttpClientContext } from 'wrappers/PaymentsApiAuthProvider';

import { DecodedParamsRoute } from './DecodedParamsRoute';

type EntityPiiRouteProps = {
  workspaceId: string;
};

const EntityPiiRoute = ({ workspaceId }: EntityPiiRouteProps) => {
  const [, setLocation] = useLocation();
  const onBack = useCallback(() => {
    setLocation(
      makePath(['w', workspaceId, 'payments', 'payment-settings'], {
        absolute: true,
      })
    );
  }, [workspaceId, setLocation]);
  return <EntityPiiPage workspaceId={workspaceId} onBack={onBack} />;
};

export const PaymentsRouter = ({
  hook,
  base,
  workspaceId,
}: Partial<RouterProps> & { workspaceId: string }): ReturnType<
  typeof Router
> => {
  const isPaymentsForRealEnabled = useFlagEnabledWithOverrides(
    'dashboard-payments-for-real'
  );
  const { hasWorkspace } = usePaymentsApiHttpClientContext();
  if (!hasWorkspace) {
    return (
      <DashboardLayout>
        <>Loading...</>
      </DashboardLayout>
    );
  }
  return (
    <DashboardLayout>
      <Suspense fallback={<>Loading...</>}>
        <Router hook={hook} base={base} key={base}>
          <Switch>
            <DecodedParamsRoute path="/users/:externalId">
              {(params) => (
                <UserDetailPage
                  workspaceId={workspaceId}
                  externalId={params.externalId}
                />
              )}
            </DecodedParamsRoute>
            <DecodedParamsRoute path="/invoices/:invoiceId">
              {(params) => (
                <InvoiceDetailPage
                  workspaceId={workspaceId}
                  invoiceId={params.invoiceId}
                />
              )}
            </DecodedParamsRoute>
            <DecodedParamsRoute path="/invoices">
              <InvoicesListPage workspaceId={workspaceId} />
            </DecodedParamsRoute>
            <DecodedParamsRoute path="/accounts/:accountId">
              {(params) => (
                <AccountDetailPage
                  workspaceId={workspaceId}
                  accountId={params.accountId}
                />
              )}
            </DecodedParamsRoute>
            <DecodedParamsRoute path="/transactions/:transactionId">
              {(params) => (
                <TransactionDetailPage
                  workspaceId={workspaceId}
                  transactionId={params.transactionId}
                />
              )}
            </DecodedParamsRoute>
            <DecodedParamsRoute path="/transactions">
              <TransactionListPage workspaceId={workspaceId} />
            </DecodedParamsRoute>
            <DecodedParamsRoute path="/catalog">
              <CatalogPage workspaceId={workspaceId} />
            </DecodedParamsRoute>
            <DecodedParamsRoute path="/create-invoice">
              <CreateInvoicePage workspaceId={workspaceId} />
            </DecodedParamsRoute>
            <DecodedParamsRoute path="/payment-settings/entity-pii">
              {isPaymentsForRealEnabled ? (
                <EntityPiiRoute workspaceId={workspaceId} />
              ) : null}
            </DecodedParamsRoute>
            <DecodedParamsRoute path="/payment-settings">
              {isPaymentsForRealEnabled ? <PaymentsPage /> : null}
            </DecodedParamsRoute>
            <DecodedParamsRoute path="/">
              <PaymentsHomePage />
            </DecodedParamsRoute>
            <Route />
          </Switch>
        </Router>
      </Suspense>
    </DashboardLayout>
  );
};
